/**
 * Low-level Google layer: gtag events, Ads conversions, Consent Mode.
 *
 * Nothing here knows about the funnel. index.js decides what to send; this
 * decides how to reach gtag safely.
 */

import Cookie from 'js-cookie';

import { removeCookieEverywhere } from '../cookies';

import { ADS_LABELS } from './events';


export const isBrowser = () => typeof window !== 'undefined';

const hasGtag = () => isBrowser() && typeof window.gtag === 'function';

/* Gatsby's webpack replaces `process.env` with `{}` plus one literal per
   GATSBY_* key, so a computed process.env[name] silently returns undefined in
   the browser bundle. Every read below must stay a literal member expression,
   and must stay inside a function so tests can mutate process.env without
   resetting modules. */
export const getAnalyticsTagId = () => process.env.GATSBY_PUBLIC_GOOGLE_ANALYTICS_TAG_ID;
export const getAdsTagId = () => process.env.GATSBY_PUBLIC_GOOGLE_ADS_TAG_ID;

export function getAdsLabel(labelKey) {
  switch (labelKey) {
    case ADS_LABELS.PURCHASE: return process.env.GATSBY_PUBLIC_GOOGLE_ADS_LABEL_PURCHASE;
    case ADS_LABELS.BEGIN_CHECKOUT: return process.env.GATSBY_PUBLIC_GOOGLE_ADS_LABEL_BEGIN_CHECKOUT;
    case ADS_LABELS.SELECT_ROOM: return process.env.GATSBY_PUBLIC_GOOGLE_ADS_LABEL_SELECT_ROOM;
    case ADS_LABELS.SEARCH: return process.env.GATSBY_PUBLIC_GOOGLE_ADS_LABEL_SEARCH;
    case ADS_LABELS.CONTACT: return process.env.GATSBY_PUBLIC_GOOGLE_ADS_LABEL_CONTACT;
    default: return undefined;
  }
}

/**
 * A GA4 event.
 *
 * send_to is not optional. html.js configures the GA4 tag and the Ads tag on
 * the same gtag instance, so an event without send_to broadcasts to both: that
 * is how a single `purchase` was reaching Google Ads as well as GA4 and
 * inflating it. Scoping every event is what stops it. For the same reason, a
 * build with no GA4 tag id sends nothing at all rather than an unscoped event
 * that would land on the Ads tag alone.
 *
 * `alsoAds` addresses the event to the Ads tag as well, explicitly. Page views
 * are the one event Ads still needs: its remarketing audiences are built from
 * them, and before this wrapper every unscoped page_view reached it. A
 * conversion never comes through here; that is pushConversion.
 */
export function pushEvent(eventName, params = {}, { alsoAds = false } = {}) {
  const analyticsTagId = getAnalyticsTagId();
  if (!hasGtag() || !analyticsTagId) return;

  const adsTagId = getAdsTagId();
  const sendTo = alsoAds && adsTagId ? [analyticsTagId, adsTagId] : analyticsTagId;

  window.gtag('event', eventName, {
    send_to: sendTo,
    page_location: window.location.href,
    ...params,
  });
}

/**
 * A Google Ads conversion.
 *
 * An unset label is the normal state on previews and on a developer machine,
 * so it degrades silently rather than sending `AW-.../undefined`.
 */
export function pushConversion(labelKey, params = {}) {
  const adsTagId = getAdsTagId();
  const label = getAdsLabel(labelKey);
  if (!hasGtag() || !adsTagId || !label) return false;

  window.gtag('event', 'conversion', {
    send_to: `${adsTagId}/${label}`,
    ...params,
  });
  return true;
}

export function pushException(description, fatal) {
  const analyticsTagId = getAnalyticsTagId();
  if (!hasGtag() || !analyticsTagId) return;
  window.gtag('event', 'exception', { send_to: analyticsTagId, description, fatal });
}

/* Removes what Google set. Google writes these with cookie_domain 'auto', so
   they are domain-scoped and a delete-write without a matching Domain does
   nothing at all; removeCookieEverywhere handles that. _gcl_au (Ads) and every
   _ga_<stream> are swept, not just the first one found. */
function deleteGoogleCookies({ analytics, marketing }) {
  const names = [];
  if (!analytics) names.push('_ga', '_gat', '_gid');
  if (!marketing) names.push('_gcl_au', '_gcl_aw', '_gcl_dc');

  const all = Cookie.get() || {};
  Object.keys(all).forEach((name) => {
    if (!analytics && name.startsWith('_ga_')) names.push(name);
  });

  names.forEach(removeCookieEverywhere);
}

export function updateGoogleConsent({ analytics, marketing }) {
  deleteGoogleCookies({ analytics, marketing });

  if (!hasGtag()) return;
  window.gtag('consent', 'update', {
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
    analytics_storage: analytics ? 'granted' : 'denied',
  });
}
